/**
 * useSoloTemplates Hook
 * Fetches all solo choreographer templates from the API with fallback to static data.
 * Returns { data, loading, error, refetch } where data is SoloTemplate[].
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { SoloTemplate } from '@/types/soloChoreographer';
import { API_ENDPOINTS } from '@/config/api';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';

interface UseSoloTemplatesReturn {
  data: SoloTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSoloTemplates(): UseSoloTemplatesReturn {
  const [data, setData] = useState<SoloTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(`${API_ENDPOINTS.soloPacks.list}?full=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.data && json.data.length > 0) {
        setData(json.data as SoloTemplate[]);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch solo templates';
      setError(msg);
      console.error('[useSoloTemplates] Fetch failed, loading static fallback chunk:', msg);
      try {
        const fallback = await import('@/features/SoloChoreographer/data/soloTemplates');
        setData(fallback.SOLO_TEMPLATES);
      } catch (fallbackErr) {
        console.error('[useSoloTemplates] Static fallback failed to load:', fallbackErr);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!fetchedRef.current) {
      fetchedRef.current = true;
      refetch();
    }
  }, [refetch]);

  return { data, loading, error, refetch };
}
